import { useEffect, useRef, useState } from 'react';
import { Upload } from 'lucide-react';
import type { Account, NetworkType } from '@mls-wallet/shared-types';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TransactionBuilderHeader } from '@/components/transaction-builder/TransactionBuilderHeader';
import { TransactionBuilderFooter } from '@/components/transaction-builder/TransactionBuilderFooter';
import { ImportedTransactionPreview } from '@/components/transaction-builder/ImportedTransactionPreview';
import { parseTransactionJson } from '@/components/transaction-builder/transaction-json-import';
import { getTransactionPolicyError } from '@/components/transaction-builder/transaction-policy-validation';
import { useTransactionBuilderStore } from '@/components/transaction-builder/useTransactionBuilderStore';
import { useTransactionBuilderPolicies } from '@/hooks/policy/use-transaction-builder-policies';
import { useAuthClient } from '@/lib/authClient';

const MAX_FILE_SIZE_BYTES = 1024 * 1024;

/** Import an entire file, then assign policies to its ordered children. */
export function TransactionBuilderJsonImportPage({
  account,
  network,
}: {
  account: Account;
  network: NetworkType;
}): React.ReactElement {
  const children = useTransactionBuilderStore(
    (state) => state.importedTransactions
  );
  const fileName = useTransactionBuilderStore(
    (state) => state.importedFileName
  );
  const setImportedTransactions = useTransactionBuilderStore(
    (state) => state.setImportedTransactions
  );
  const clearImportedTransactions = useTransactionBuilderStore(
    (state) => state.clearImportedTransactions
  );
  const setPolicy = useTransactionBuilderStore(
    (state) => state.setImportedTransactionPolicy
  );
  const {
    policies,
    isLoading,
    error: policiesError,
  } = useTransactionBuilderPolicies({
    accountId: account.id,
    networkId: network,
  });
  const { user } = useAuthClient();
  const [fileError, setFileError] = useState<string | null>(null);
  const [isReading, setIsReading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const readId = useRef(0);

  // Reads belong to this page and selection. Replacing a file or leaving the
  // page revokes ownership, including when the old read rejects afterwards.
  useEffect(
    () => () => {
      readId.current += 1;
    },
    []
  );

  const handleFile = async (file: File) => {
    const currentRead = ++readId.current;
    setFileError(null);
    clearImportedTransactions();
    setIsReading(true);
    // Case: install a complete valid file, or show its read/validation error without retaining an old draft.
    try {
      // Case: unsupported file types cannot produce an imported draft.
      if (!file.name.toLowerCase().endsWith('.json'))
        throw new Error('Choose a .json file.');
      // Case: oversized files are rejected before reading their contents into memory.
      if (file.size > MAX_FILE_SIZE_BYTES)
        throw new Error('The JSON file must be smaller than 1 MB.');
      const contents = await file.text();
      // Case: a newer import or navigation revoked this read's right to update the draft.
      if (currentRead !== readId.current) return;
      setImportedTransactions(
        parseTransactionJson(contents, network),
        file.name
      );
    } catch (error) {
      // Case: only the active file read may report an error on this import page.
      if (currentRead === readId.current)
        setFileError(
          error instanceof Error
            ? error.message
            : 'The JSON file could not be read.'
        );
    } finally {
      // Case: an older read must not clear the loading state of its replacement.
      if (currentRead === readId.current) setIsReading(false);
    }
  };

  const context = {
    multichainAccountId: account.id,
    networkId: network,
    userId: user?.id ?? '',
  };
  const canContinue = Boolean(
    user &&
      !isReading &&
      !isLoading &&
      !policiesError &&
      children?.length &&
      children.every((child) => {
        const policy = policies?.find(({ id }) => id === child.policy?.id);
        return policy && !getTransactionPolicyError(child, policy, context);
      })
  );

  const handleContinue = () => {
    // Case: incomplete imports or currently ineligible policies must remain on the import step.
    if (!canContinue || !children) return;
    // Refresh the selected policy snapshots before leaving the import step.
    children.forEach((child) =>
      setPolicy(
        child.id,
        policies?.find(({ id }) => id === child.policy?.id)
      )
    );
    useTransactionBuilderStore.getState().appendImportedTransactions();
  };

  return (
    <>
      <TransactionBuilderHeader
        title="Import transactions from JSON"
        showBackButton
      />
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 px-4 py-8">
        <div className="rounded-2xl border bg-background p-6">
          <p className="mb-4 text-sm text-muted-foreground">
            Upload a raw transaction, an array of transactions, or a Safe
            Transaction Builder file. All transactions use the account and
            network shown above, in file order.
          </p>
          <button
            type="button"
            className="flex min-h-40 w-full flex-col items-center justify-center gap-3 rounded-xl border border-dashed bg-muted/50 p-6 hover:bg-muted"
            onClick={() => fileInput.current?.click()}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              const file = event.dataTransfer.files[0];
              // Case: only a dropped file starts a replacement import.
              if (file) void handleFile(file);
            }}
          >
            <Upload className="size-5" aria-hidden="true" />
            <span className="font-medium">
              {isReading
                ? 'Reading JSON…'
                : (fileName ?? 'Drop a JSON file here or choose a file')}
            </span>
          </button>
          <input
            ref={fileInput}
            type="file"
            aria-label="Transaction JSON file"
            accept=".json,application/json"
            className="sr-only"
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Case: dismissing the file picker without a selection leaves the draft unchanged.
              if (file) void handleFile(file);
              event.target.value = '';
            }}
          />
          <details className="mt-4 text-sm">
            <summary className="cursor-pointer">Example JSON</summary>
            <pre className="mt-3 overflow-x-auto rounded-lg bg-muted p-4 text-xs">
              {JSON.stringify(
                {
                  chainId: String(network),
                  transactions: [
                    {
                      to: '0x1111111111111111111111111111111111111111',
                      value: '1000000000000000',
                      data: '0x',
                    },
                    {
                      to: '0x2222222222222222222222222222222222222222',
                      value: '0',
                      data: '0x12345678',
                    },
                  ],
                },
                null,
                2
              )}
            </pre>
            <p className="mt-2 text-muted-foreground">
              Value is an integer string in wei. Data must be encoded calldata,
              or 0x for a native send. Imported function names and policy
              metadata are not used.
            </p>
          </details>
        </div>

        {fileError && (
          <p
            role="alert"
            className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive"
          >
            {fileError}
          </p>
        )}
        {children && (
          <section aria-label="Imported transactions" className="space-y-4">
            <h2 className="font-semibold">Transactions ({children.length})</h2>
            {isLoading && <p role="status">Loading policies…</p>}
            {policiesError && (
              <p role="alert" className="text-destructive">
                Policies could not be loaded. Try again before continuing.
              </p>
            )}
            {children.map((child, index) => {
              const selectedPolicy = policies?.find(
                ({ id }) => id === child.policy?.id
              );
              const selectionError =
                child.policy && !isLoading
                  ? selectedPolicy
                    ? getTransactionPolicyError(child, selectedPolicy, context)
                    : 'This policy is no longer available. Choose another policy.'
                  : null;
              const choices = (policies ?? []).map((policy) => ({
                policy,
                error: getTransactionPolicyError(child, policy, context),
              }));
              return (
                <article
                  key={child.id}
                  className="space-y-4 rounded-2xl border bg-background p-6"
                >
                  <h3 className="font-medium">Transaction {index + 1}</h3>
                  <ImportedTransactionPreview transaction={child} />
                  <div className="space-y-2">
                    <label
                      htmlFor={`policy-${child.id}`}
                      className="text-sm font-medium"
                    >
                      Policy for transaction {index + 1}
                    </label>
                    <Select
                      value={selectedPolicy?.id ?? ''}
                      disabled={isLoading || !!policiesError || !user}
                      onValueChange={(policyId) =>
                        setPolicy(
                          child.id,
                          policies?.find(({ id }) => id === policyId)
                        )
                      }
                    >
                      <SelectTrigger
                        id={`policy-${child.id}`}
                        className="h-10 bg-background"
                      >
                        <SelectValue placeholder="Choose a policy">
                          <span>{selectedPolicy?.name}</span>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {choices.map(({ policy, error }) => (
                          <SelectItem
                            key={policy.id}
                            value={policy.id}
                            textValue={policy.name}
                            disabled={!!error}
                            className="whitespace-normal"
                          >
                            <span className="block">{policy.name}</span>
                            {error && (
                              <span className="mt-1 block text-xs text-destructive">
                                {error}
                              </span>
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!isLoading &&
                      !policiesError &&
                      !choices.some(({ error }) => !error) && (
                        <p className="text-sm text-muted-foreground">
                          No eligible policies are available for this
                          transaction.
                        </p>
                      )}
                    {selectionError && (
                      <p role="alert" className="text-sm text-destructive">
                        {selectionError}
                      </p>
                    )}
                  </div>
                </article>
              );
            })}
            <Button
              variant="outline"
              onClick={() => {
                ++readId.current;
                clearImportedTransactions();
                setFileError(null);
                setIsReading(false);
              }}
            >
              Clear import
            </Button>
          </section>
        )}
      </div>
      <TransactionBuilderFooter
        onContinue={handleContinue}
        continueButtonText="Add to batch"
        continueButtonDisabled={!canContinue}
      />
    </>
  );
}
